import { useState } from 'react';
import type { ChatSession } from '../../types';
import { Button } from '../ui';
import { cn } from '../../utils';
import {
  MessageSquarePlus,
  Trash2,
  X,
  Pencil,
  Check,
  MessageSquare,
  EyeOff,
} from 'lucide-react';

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: ChatSession[];
  activeSessionId: string | null;
  onNewChat: () => void;
  /** Start a private ("incognito") chat — brain off, not saved anywhere. */
  onNewPrivateChat?: () => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, title: string) => void;
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - timestamp;

  // Today
  if (date.toDateString() === now.toDateString()) {
    return 'Today';
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  // This week
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString([], { weekday: 'long' });
  }

  // This year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  // Older
  return date.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function groupSessionsByDate(
  sessions: ChatSession[]
): { label: string; sessions: ChatSession[] }[] {
  const groups: { [key: string]: ChatSession[] } = {};

  for (const session of sessions) {
    const label = formatDate(session.updatedAt);
    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(session);
  }

  // Convert to array and sort by most recent
  return Object.entries(groups)
    .map(([label, sessions]) => ({ label, sessions }))
    .sort((a, b) => {
      const aTime = a.sessions[0]?.updatedAt || 0;
      const bTime = b.sessions[0]?.updatedAt || 0;
      return bTime - aTime;
    });
}

function SessionItem({
  session,
  isActive,
  onSelect,
  onDelete,
  onRename,
}: {
  session: ChatSession;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(session.title);

  const handleRename = () => {
    if (editTitle.trim() && editTitle !== session.title) {
      onRename(editTitle.trim());
    }
    setIsEditing(false);
  };

  return (
    // Not a <button>: this row contains its own rename/delete buttons, and
    // nesting interactive elements inside a button is invalid HTML. So the row
    // opts into button semantics explicitly — without this it was mouse-only,
    // and the whole chat history was unreachable by keyboard (tdd-0074).
    //
    // Space is preventDefault'd so selecting a conversation doesn't also scroll
    // the sidebar. While editing, the row yields entirely to the input.
    <div
      className={cn(
        'group flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'hover:bg-accent/50 text-muted-foreground hover:text-foreground'
      )}
      role={isEditing ? undefined : 'button'}
      tabIndex={isEditing ? undefined : 0}
      aria-current={isActive ? 'true' : undefined}
      onClick={() => !isEditing && onSelect()}
      onKeyDown={(e) => {
        if (isEditing) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
      {isEditing ? (
        <div className="flex-1 flex items-center gap-1">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            onClick={(e) => e.stopPropagation()}
            // The cleared outline is replaced by a focus-visible underline —
            // without it, keyboard users renaming a chat lose the focus
            // indicator entirely (tdd-0074).
            className="flex-1 bg-transparent border-b border-foreground/20 outline-none text-foreground focus-visible:border-primary focus-visible:border-b-2"
            aria-label="Rename conversation"
            autoFocus
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRename();
            }}
            aria-label="Save name"
            className="p-0.5 hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Check className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <>
          <span className="flex-1 truncate">{session.title}</span>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditTitle(session.title);
                setIsEditing(true);
              }}
              // Icon-only, and the label must name WHICH conversation — a
              // sidebar of identical "Rename" buttons is unusable in a screen
              // reader's element list.
              aria-label={`Rename "${session.title}"`}
              className="p-1 hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              aria-label={`Delete "${session.title}"`}
              className="p-1 hover:text-destructive rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function ChatSidebar({
  isOpen,
  onClose,
  sessions,
  activeSessionId,
  onNewChat,
  onNewPrivateChat,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
}: ChatSidebarProps) {
  const groupedSessions = groupSessionsByDate(sessions);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed left-0 top-0 bottom-0 z-50 w-72 bg-background border-r transform transition-transform duration-200 ease-in-out flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <h2 className="font-semibold text-sm">Chat History</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* New Chat Buttons */}
        <div className="p-3 border-b space-y-2">
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={() => {
              onNewChat();
              onClose();
            }}
          >
            <MessageSquarePlus className="h-4 w-4" />
            New Chat
          </Button>
          {onNewPrivateChat && (
            <Button
              variant="outline"
              className="w-full justify-start gap-2 text-violet-600 dark:text-violet-300 border-violet-500/30 hover:bg-violet-500/10"
              onClick={() => {
                onNewPrivateChat();
                onClose();
              }}
            >
              <EyeOff className="h-4 w-4" />
              Private Chat
            </Button>
          )}
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto p-2">
          {sessions.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-8">
              No conversations yet
            </div>
          ) : (
            <div className="space-y-4">
              {groupedSessions.map((group) => (
                <div key={group.label}>
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {group.sessions.map((session) => (
                      <SessionItem
                        key={session.id}
                        session={session}
                        isActive={session.id === activeSessionId}
                        onSelect={() => {
                          onSelectSession(session.id);
                          onClose();
                        }}
                        onDelete={() => onDeleteSession(session.id)}
                        onRename={(title) => onRenameSession(session.id, title)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
